/* ═══════════════════════════════════════════════════════════════════
   COREVIAN UNIVERSITY — AUTHORIZATION / READINESS EXPLANATION ENGINE
   ───────────────────────────────────────────────────────────────────
   PURE, READ-ONLY. This file contains no Supabase client, no .rpc(),
   .insert(), .update(), or .delete() call, and no reference to any
   authorization-mutating function anywhere in the codebase. It cannot
   grant, revoke, alter, or otherwise touch Operational Authorization
   state — it only takes an already-loaded qualification record
   (a row from workforceReadinessRows, built entirely by existing,
   unmodified code in corevian-university-2.js) and returns a plain
   descriptive object explaining that record's CURRENT state.

   determination/authorized are copied directly from the row's own
   overall_status — the single authoritative Workforce Readiness
   calculation in loadWorkforceReadinessWorkspace(). This engine never
   recomputes that value independently, so it cannot disagree with or
   compete with the real determination.

   It distinguishes, where the loaded records actually support it:
     - prerequisite qualification controls incomplete
       (Final Assessment / Competency / Field Readiness / Endorsement)
     - prerequisites complete but Operational Authorization not issued
     - Operational Authorization suspended
     - Operational Authorization revoked
     - Operational Authorization record otherwise not current

   It never infers a reason the loaded data doesn't actually support.
   ═══════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  /**
   * explainQualificationStatus(employee)
   *
   * @param {object} employee - one row from workforceReadinessRows,
   *   already loaded by loadWorkforceReadinessWorkspace(). This
   *   function performs no data fetching of its own.
   *
   * @returns {{
   *   determination: 'ready'|'action_required'|'not_authorized',
   *   authorized: boolean,
   *   summary: string,
   *   serviceScope: string[],
   *   blockers: Array<{control:string, status:string, explanation:string, nextAction:string}>,
   *   warnings: Array<{control:string, status:string, explanation:string, nextAction:string}>,
   *   nextActions: string[],
   *   evaluatedAt: string,
   *   sourceRow: {user_id:string, full_name:string}
   * }}
   */
  function explainQualificationStatus(employee) {
    if (!employee || typeof employee !== 'object') {
      return {
        determination: 'not_authorized',
        authorized: false,
        summary: 'No qualification record was supplied.',
        serviceScope: [],
        blockers: [{
          control: 'Record',
          status: 'MISSING',
          explanation: 'No qualification record was supplied to the explanation engine.',
          nextAction: 'Confirm the employee record loaded correctly before requesting an explanation.'
        }],
        warnings: [],
        nextActions: ['Confirm the employee record loaded correctly before requesting an explanation.'],
        evaluatedAt: new Date().toISOString(),
        sourceRow: null
      };
    }

    // determination is copied verbatim — never recomputed.
    const determination = employee.overall_status || 'not_authorized';
    const authorized = determination !== 'not_authorized';

    const blockers = [];
    const warnings = [];

    const auth = employee.latest_authorization || null;
    const authStatus = auth ? auth.status : null;
    const authorizedEndorsements = employee.authorized_endorsements_current || [];

    if (determination === 'not_authorized') {
      // ── Prerequisite qualification controls — evidence-based only ──
      if (!employee.final_passed) {
        blockers.push({
          control: 'Final Assessment',
          status: 'HOLD',
          explanation: 'The employee has not passed the required Employee Final Assessment.',
          nextAction: 'Employee must pass the Employee Final Assessment.'
        });
      }

      if (!employee.competencies_complete) {
        const have = Number(employee.competent_count || 0);
        const need = Number(employee.required_count || 0);
        blockers.push({
          control: 'Hands-On Competency',
          status: 'NOT QUALIFIED (' + have + ' / ' + need + ')',
          explanation: 'One or more required hands-on competencies have not been recorded as Competent.',
          nextAction: 'A qualified evaluator must complete and record the remaining required competency evaluations.'
        });
      }

      if (!employee.field_ready) {
        const observedResult = employee.field_record && employee.field_record.readiness_result
          ? employee.field_record.readiness_result
          : 'not recorded';
        blockers.push({
          control: 'Field Readiness',
          status: 'NOT READY (' + observedResult + ')',
          explanation: 'Supervised field training has not produced a Ready result.',
          nextAction: 'Leadership must complete and record a Ready field-training observation.'
        });
      }

      if (!employee.endorsements_documented) {
        blockers.push({
          control: 'Operational Endorsement',
          status: 'NONE DOCUMENTED',
          explanation: 'No operational endorsement has been documented for this employee.',
          nextAction: 'Leadership must record an endorsement decision for an applicable service category.'
        });
      } else if (authorizedEndorsements.length === 0) {
        blockers.push({
          control: 'Operational Endorsement',
          status: 'DOCUMENTED, NONE AUTHORIZED',
          explanation: 'Endorsement records exist but none currently carry an Authorized status.',
          nextAction: 'Leadership must review and authorize an applicable service-category endorsement.'
        });
      }

      const prerequisitesComplete =
        !!employee.final_passed &&
        !!employee.competencies_complete &&
        !!employee.field_ready &&
        !!employee.endorsements_documented &&
        authorizedEndorsements.length > 0;

      // ── Operational Authorization record itself — always stated
      // factually when not authorized, regardless of whether other
      // prerequisite blockers are also present. This is the record's
      // current true state, not an inferred cause. ──
      if (!auth) {
        blockers.push({
          control: 'Operational Authorization',
          status: 'NOT ISSUED',
          explanation: prerequisitesComplete
            ? 'All prerequisite qualification controls are satisfied. A final Operational Authorization decision has not yet been recorded.'
            : 'No Operational Authorization decision has been recorded.',
          nextAction: 'Leadership must complete the final Operational Authorization review and issue a decision.'
        });
      } else if (authStatus === 'suspended') {
        blockers.push({
          control: 'Operational Authorization',
          status: 'SUSPENDED',
          explanation: 'Operational Authorization is currently suspended.' + (auth.reason ? ' Reason on file: ' + auth.reason : ''),
          nextAction: 'Leadership must review and resolve the suspension before independent assignment can resume.'
        });
      } else if (authStatus === 'revoked') {
        blockers.push({
          control: 'Operational Authorization',
          status: 'REVOKED',
          explanation: 'Operational Authorization has been revoked.' + (auth.reason ? ' Reason on file: ' + auth.reason : ''),
          nextAction: 'A new Operational Authorization decision is required before independent assignment can be considered.'
        });
      } else if (authStatus !== 'authorized') {
        // A record exists but its status is something other than the
        // three states above and 'authorized' — stated plainly rather
        // than guessed at.
        blockers.push({
          control: 'Operational Authorization',
          status: String(authStatus || 'UNKNOWN').toUpperCase(),
          explanation: 'The current Operational Authorization record does not reflect an authorized status.',
          nextAction: 'Leadership must review the current authorization record.'
        });
      }
      // Note: if every prerequisite is complete AND authStatus === 'authorized',
      // overall_status would already be 'ready' or 'action_required', not
      // 'not_authorized' — that combination cannot occur given the real
      // overall_status formula, so no branch is needed for it here.
    }

    // ── Warnings — non-blocking, relevant regardless of determination ──
    const renewalCounts = employee.renewal_counts || {};
    if (renewalCounts.expired > 0) {
      warnings.push({
        control: 'Renewal / Recertification',
        status: 'EXPIRED (' + renewalCounts.expired + ')',
        explanation: renewalCounts.expired + ' recertification item(s) have expired.',
        nextAction: 'Leadership must record updated recertification evidence.'
      });
    }
    if (renewalCounts.due_soon > 0) {
      warnings.push({
        control: 'Renewal / Recertification',
        status: 'DUE SOON (' + renewalCounts.due_soon + ')',
        explanation: renewalCounts.due_soon + ' recertification item(s) are due soon.',
        nextAction: 'Schedule renewal before the due date to avoid a lapse.'
      });
    }

    const documentReadiness = employee.document_readiness;
    if (documentReadiness === 'overdue') {
      warnings.push({
        control: 'Controlled Documents',
        status: 'OVERDUE',
        explanation: 'One or more required document acknowledgments are overdue.',
        nextAction: 'Employee must review and acknowledge the outstanding controlled document(s).'
      });
    } else if (documentReadiness === 'action_required') {
      warnings.push({
        control: 'Controlled Documents',
        status: 'ACTION REQUIRED',
        explanation: 'One or more required document acknowledgments are outstanding.',
        nextAction: 'Employee must review and acknowledge the outstanding controlled document(s).'
      });
    }

    let summary;
    if (determination === 'not_authorized') {
      summary = 'NOT AUTHORIZED — ' + blockers.length + ' unresolved control' + (blockers.length === 1 ? '' : 's');
    } else if (determination === 'action_required') {
      summary = 'AUTHORIZED — ' + warnings.length + ' item' + (warnings.length === 1 ? '' : 's') + ' requiring attention';
    } else {
      summary = 'AUTHORIZED — all required controls are satisfied for the current service scope';
    }

    const nextActions = Array.from(new Set(blockers.concat(warnings).map(function (item) { return item.nextAction; })));

    return {
      determination: determination,
      authorized: authorized,
      summary: summary,
      serviceScope: authorizedEndorsements.map(function (item) { return item.endorsement_name; }),
      blockers: blockers,
      warnings: warnings,
      nextActions: nextActions,
      evaluatedAt: new Date().toISOString(),
      sourceRow: { user_id: employee.user_id, full_name: employee.full_name }
    };
  }

  window.explainQualificationStatus = explainQualificationStatus;
})();